/**
 * useIndumentariaPlazos — shared logic for indumentaria installment pricing
 *
 * Extracted from ResumenCompraComponent (frontend + backend) to avoid
 * duplication of indumentaria installment calculation logic.
 *
 * Both functions are pure — receive data, return results.
 * Compatible with Options API and Composition API.
 */

export function useIndumentariaPlazos() {
    /**
     * Calculate the proportional indumentaria cost for the first installment.
     *
     * When indumentaria_plazos is true, distributes the first installment's
     * indumentaria portion (plazos[0].importe_indumentaria) proportionally
     * across all selected indumentaria items based on their full price.
     *
     * When indumentaria_plazos is false, returns the full item price
     * (paid upfront).
     *
     * @param {Object} indumentaria - The indumentaria item (with precio_total, incluida, unidades)
     * @param {Array|null} plazos - Installment array from API
     * @param {number} totalIndumentarias - Sum of all indumentaria full prices
     * @param {boolean} indumentariaPlazos - Whether indumentaria is paid in installments
     * @returns {number} The amount to display
     */
    function getIndumentariaImportePlazo(indumentaria, plazos, totalIndumentarias, indumentariaPlazos) {
        if (!plazos?.length) return parseFloat(indumentaria.precio_total);
        if (indumentariaPlazos) {
            if (totalIndumentarias === 0) return 0;
            const itemPrice = (indumentaria.incluida && indumentaria.unidades === 1) ? 0 : parseFloat(indumentaria.precio_total);
            return (itemPrice / totalIndumentarias) * plazos[0].importe_indumentaria;
        }
        return (indumentaria.incluida && indumentaria.unidades === 1) ? 0 : parseFloat(indumentaria.precio_total);
    }

    /**
     * Get the category price to display as the first installment amount.
     *
     * When there are indumentarias (split or upfront), shows only the base
     * portion (plazos[0].importe_base) since indumentaria is itemized
     * separately in the indumentaria rows.
     *
     * When there are no indumentarias, shows the full first installment
     * total (plazos[0].importe == plazos[0].importe_base).
     *
     * @param {Array|null} plazos - Installment array from API
     * @param {number} itemPrecio - The category base price (fallback when no plazos)
     * @param {boolean} hasIndumentarias - Whether there are selected indumentaria items
     * @returns {number} The amount to display
     */
    function getPrecioBasePrimerPlazo(plazos, itemPrecio, hasIndumentarias) {
        if (!plazos?.length) return itemPrecio;
        return hasIndumentarias ? plazos[0].importe_base : plazos[0].importe;
    }

    return { getIndumentariaImportePlazo, getPrecioBasePrimerPlazo };
}
